import { usePaginatedQuery } from "convex/react";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	type LucideIcon,
	MailIcon,
	MessageSquareIcon,
	PhoneIcon,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { ClioCommunicationItem } from "./types";

interface MatterCommunicationsProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

const ICON_BY_TYPE: Record<ClioCommunicationItem["type"], LucideIcon> = {
	EmailCommunication: MailIcon,
	PhoneCommunication: PhoneIcon,
	CommunicationNote: MessageSquareIcon,
};

const LABEL_BY_TYPE: Record<ClioCommunicationItem["type"], string> = {
	EmailCommunication: "Email",
	PhoneCommunication: "Phone",
	CommunicationNote: "Note",
};

function CommunicationRow({ comm }: { comm: ClioCommunicationItem }) {
	const [expanded, setExpanded] = useState(false);
	const Icon = ICON_BY_TYPE[comm.type];
	const hasBody = Boolean(comm.body && comm.body.trim().length > 0);
	const ChevronIcon = expanded ? ChevronDownIcon : ChevronRightIcon;
	return (
		<div className="flex gap-3 p-4">
			<Icon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<div className="flex items-center gap-2">
					<Badge variant="outline" className="font-normal">
						{LABEL_BY_TYPE[comm.type]}
					</Badge>
					<span className="truncate text-sm font-semibold">
						{comm.subject ?? "(no subject)"}
					</span>
					<span className="ml-auto shrink-0 text-xs text-muted-foreground">
						{new Date(comm.date).toLocaleString()}
					</span>
				</div>
				<div className="text-xs text-muted-foreground">
					<span className="font-medium text-foreground/80">
						{comm.senders
							.map((s) => s.name ?? "Unknown")
							.filter(Boolean)
							.join(", ") || "—"}
					</span>
					{comm.receivers.length > 0 && (
						<>
							{" → "}
							{comm.receivers
								.map((r) => r.name ?? "Unknown")
								.filter(Boolean)
								.join(", ")}
						</>
					)}
				</div>
				{hasBody && (
					<>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => setExpanded((v) => !v)}
							aria-expanded={expanded}
							className="h-auto w-fit gap-1 px-2 py-1 text-xs font-normal text-muted-foreground hover:text-foreground"
						>
							<ChevronIcon className="h-3 w-3" />
							{expanded ? "Collapse" : "Show full message"}
						</Button>
						<div
							className={
								expanded
									? "whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-sm text-foreground/90"
									: "line-clamp-3 whitespace-pre-wrap break-words text-sm text-foreground/80"
							}
						>
							{comm.body}
						</div>
					</>
				)}
			</div>
		</div>
	);
}

export function MatterCommunications({
	connectionId,
	clioMatterId,
}: MatterCommunicationsProps) {
	const pagination = usePaginatedQuery(
		api.clio.queries.listCommunications,
		{ connectionId, clioMatterId },
		{ initialNumItems: 25 },
	);

	if (
		pagination.status === "LoadingFirstPage" &&
		pagination.results.length === 0
	) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading communications…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (pagination.results.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>
						No communications logged on this matter.
					</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col divide-y">
				{pagination.results.map((comm) => (
					<CommunicationRow key={comm._id} comm={comm} />
				))}
				{pagination.status === "CanLoadMore" && (
					<div className="p-3">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={() => pagination.loadMore(25)}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
